/**
 * Centralized configuration for the Stash frontend application
 * All environment variables should be accessed through this module
 */

// Validate required environment variables at build time
function getRequiredEnv(key: string): string {
  const value = import.meta.env[key];
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

function getOptionalEnv(key: string, defaultValue: string): string {
  return import.meta.env[key] || defaultValue;
}

// Parse boolean environment variables
function getBooleanEnv(key: string, defaultValue: boolean): boolean {
  const value = import.meta.env[key];
  if (value === undefined || value === '') {
    return defaultValue;
  }
  return value === 'true' || value === '1';
}

/**
 * Application configuration
 *
 * IMPORTANT: All configuration values should have sensible defaults
 * that work in production. Development-specific values should be
 * set via .env files, not hardcoded here.
 */
export const config = {
  /**
   * API Configuration
   */
  api: {
    // Base URL for API requests
    // MUST be set via VITE_API_URL environment variable
    // NO hardcoded development URLs
    baseUrl: (() => {
      const url = import.meta.env.VITE_API_URL;
      if (!url) {
        console.error('FATAL: VITE_API_URL environment variable is not set');
        console.error('Please set VITE_API_URL in your .env file or environment');
        throw new Error('VITE_API_URL is required');
      }
      return url;
    })(),

    // API request timeout in milliseconds
    timeout: parseInt(getOptionalEnv('VITE_API_TIMEOUT', '30000'), 10),
  },

  /**
   * Upload Configuration
   */
  upload: {
    // Threshold for switching to chunked uploads (in bytes)
    chunkThreshold: parseInt(
      getOptionalEnv('VITE_UPLOAD_CHUNK_THRESHOLD', '10485760'), // 10MB default
      10
    ),

    // Chunk size for chunked uploads (in bytes)
    chunkSize: parseInt(
      getOptionalEnv('VITE_UPLOAD_CHUNK_SIZE', '5242880'), // 5MB default
      10
    ),

    // Maximum upload size (in bytes)
    maxSize: parseInt(
      getOptionalEnv('VITE_MAX_UPLOAD_SIZE', '536870912'), // 512MB default
      10
    ),

    // Maximum number of concurrent uploads
    maxConcurrent: parseInt(
      getOptionalEnv('VITE_MAX_CONCURRENT_UPLOADS', '3'),
      10
    ),
  },

  /**
   * Application Configuration
   */
  app: {
    // Application name
    name: getOptionalEnv('VITE_APP_NAME', 'Stash'),

    // Environment (development, staging, production)
    environment: getOptionalEnv('VITE_ENVIRONMENT', 'production'),

    // Enable debug mode
    debug: getBooleanEnv('VITE_DEBUG', false),

    // Log level (debug, info, warn, error, silent)
    logLevel: getOptionalEnv('VITE_LOG_LEVEL', 'warn') as 'debug' | 'info' | 'warn' | 'error' | 'silent',
  },

  /**
   * Feature Flags
   */
  features: {
    // Enable notifications via SSE
    notifications: getBooleanEnv('VITE_FEATURE_NOTIFICATIONS', true),

    // Enable TOTP two-factor authentication
    totp: getBooleanEnv('VITE_FEATURE_TOTP', true),

    // Enable file sharing
    sharing: getBooleanEnv('VITE_FEATURE_SHARING', true),

    // Enable search
    search: getBooleanEnv('VITE_FEATURE_SEARCH', false),
  },
} as const;

/**
 * Validate configuration for production environment
 */
export function validateProductionConfig(): void {
  const { api, app } = config;

  // Only run validation in production
  if (app.environment !== 'production') {
    return;
  }

  // Validate no development URLs in production
  const developmentPatterns = ['localhost', '127.0.0.1', '.test'];

  for (const pattern of developmentPatterns) {
    if (api.baseUrl.includes(pattern)) {
      throw new Error(
        `Development URL pattern '${pattern}' detected in API base URL: ${api.baseUrl}. ` +
        'This is not allowed in production. Please set VITE_API_URL to your production API URL.'
      );
    }
  }

  // Validate HTTPS in production
  if (!api.baseUrl.startsWith('https://')) {
    console.warn(
      'WARNING: API base URL does not use HTTPS in production. ' +
      'This is insecure and not recommended.'
    );
  }
}

/**
 * Display current configuration (useful for debugging)
 */
export function displayConfig(): void {
  if (!config.app.debug) {
    return;
  }

  console.group('=== Stash Frontend Configuration ===');
  console.log('Environment:', config.app.environment);
  console.log('API Base URL:', config.api.baseUrl);
  console.log('API Timeout:', config.api.timeout, 'ms');
  console.log('Upload Chunk Threshold:', (config.upload.chunkThreshold / 1024 / 1024).toFixed(2), 'MB');
  console.log('Upload Chunk Size:', (config.upload.chunkSize / 1024 / 1024).toFixed(2), 'MB');
  console.log('Max Upload Size:', (config.upload.maxSize / 1024 / 1024).toFixed(2), 'MB');
  console.log('Max Concurrent Uploads:', config.upload.maxConcurrent);
  console.log('Features:', config.features);
  console.groupEnd();
}

// Run validation on module load
if (typeof window !== 'undefined') {
  try {
    validateProductionConfig();
    displayConfig();
  } catch (error) {
    console.error('Configuration validation failed:', error);
    throw error;
  }
}
